import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { intakeService, usersService, tasksService, auditLogService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  BlackButton,
} from "@/components/ui/alert-dialog";
import { 
  Search, 
  Loader2, 
  Filter,
  FileText,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Route,
  ArrowUpRight,
  Plus,
  Trash2
} from "lucide-react";
import NewIntakeForm from "@/components/NewIntakeForm";

const tabs = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "score", label: "Score" },
  { id: "awaiting", label: "Awaiting Approvals" },
  { id: "approved", label: "Approved" },
  { id: "routed", label: "Routed" },
  { id: "rejected", label: "Rejected" },
];

const priorityColors = {
  low: "bg-gray-100 text-gray-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
};

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  awaiting: "bg-purple-100 text-purple-700",
  approved: "bg-green-100 text-green-700",
  routed: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
};

const getStatusLabel = (status) => {
  switch(status) {
    case "pending": return "New";
    case "awaiting": return "Awaiting Approval";
    case "approved": return "Approved";
    case "routed": return "In Progress";
    case "rejected": return "Rejected";
    default: return status || "New";
  }
};

const IntakeQueue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [intakes, setIntakes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [showNewIntakeForm, setShowNewIntakeForm] = useState(false);

  const canDelete = userData?.role === "COO" || userData?.role === "CEO" || userData?.role === "Team Lead" || userData?.role === "Marketing Team Lead" || userData?.role === "Assistant Team Lead" || userData?.permissions?.includes("all");
  const canReassign = userData?.role === "COO" || userData?.role === "CEO" || userData?.permissions?.includes("all");
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [reassignNote, setReassignNote] = useState("");

  const handleDelete = async (id) => {
    try {
      const intakeToDelete = intakes.find(i => i.id === id);
      
      // Find and delete the associated task first
      const allTasks = await tasksService.getAll();
      const taskToDelete = allTasks.find(t => t.intakeId === id);
      if (taskToDelete) {
        await tasksService.delete(taskToDelete.id);
      }
      
      // Then delete the intake
      await intakeService.delete(id);
      
      // Log the deletion
      await auditLogService.logIntakeDeleted(
        id,
        intakeToDelete?.title || "Untitled",
        userData?.id,
        userData?.name || "Unknown User"
      );
      
      setIntakes(intakes.filter(i => i.id !== id));
    } catch (error) {
      console.error("Error deleting intake:", error);
    }
  };

  const handleReassign = async () => {
    if (!selectedIntake || !reassignNote.trim()) return;
    try {
      await intakeService.update(selectedIntake.id, {
        status: "awaiting",
        reassignedAt: new Date(),
        reassignNote: reassignNote,
        reassignedBy: userData?.id
      });

      // Log the status change
      await auditLogService.logIntakeStatusChanged(
        selectedIntake.id,
        selectedIntake.title,
        selectedIntake.status,
        "awaiting",
        userData?.id,
        userData?.name || "Unknown User"
      );

      const existingTask = await tasksService.getAll();
      const taskToUpdate = existingTask.find(t => t.intakeId === selectedIntake.id);
      if (taskToUpdate) {
        await tasksService.update(taskToUpdate.id, {
          status: "Pending Approval",
          reassignNote: reassignNote
        });
      }

      setShowReassignDialog(false);
      setSelectedIntake(null);
      setReassignNote("");
      
      const [intakesData, usersData] = await Promise.all([
        intakeService.getAll(),
        usersService.getAll()
      ]);
      setIntakes(intakesData);
      setUsers(usersData);
    } catch (error) {
      console.error("Error reassigning intake:", error);
    }
  };

  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setShowNewIntakeForm(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [intakesData, usersData] = await Promise.all([
          intakeService.getAll(),
          usersService.getAll()
        ]);
        setIntakes(intakesData);
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return "Unassigned";
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.name || "Unassigned";
  };

  const filteredIntakes = intakes.filter(intake => {
    const matchesSearch = 
      intake.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      intake.requestType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      intake.department?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "new") return matchesSearch && intake.status === "pending";
    if (activeTab === "score") return matchesSearch && intake.score > 0;
    if (activeTab === "awaiting") return matchesSearch && intake.status === "awaiting";
    if (activeTab === "approved") return matchesSearch && intake.status === "approved";
    if (activeTab === "routed") return matchesSearch && intake.status === "routed";
    if (activeTab === "rejected") return matchesSearch && intake.status === "rejected";
    return matchesSearch;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-action-blue" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy">Master Intake Queue</h1>
          <p className="text-gray-500 flex items-center gap-2">
            <Route className="w-4 h-4" />
            Manager Routes • Incoming Requests
          </p>
        </div>
        <Button
          onClick={() => setShowNewIntakeForm(true)}
          className="bg-navy hover:bg-navy-light text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Intake
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-1 overflow-x-auto flex-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? "bg-navy text-white"
                      : "bg-slate-100 text-gray-600 hover:bg-slate-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="relative min-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search intakes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Task</TableHead>
                <TableHead className="font-semibold">Department</TableHead>
                <TableHead className="font-semibold">Priority</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">SLA</TableHead>
                <TableHead className="font-semibold">Owner</TableHead>
                {(canDelete || canReassign) && <TableHead className="font-semibold">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredIntakes.length > 0 ? (
                filteredIntakes.map((intake) => (
                  <TableRow key={intake.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        {intake.requestType || "Request"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-navy">{intake.title || "Untitled"}</p>
                        {intake.description && (
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">
                            {intake.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{intake.requestingDepartment || "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        priorityColors[intake.priority] || priorityColors.low
                      }`}>
                        {intake.priority || "Low"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        statusColors[intake.status] || statusColors.pending
                      }`}>
                        {getStatusLabel(intake.status)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {intake.deadline ? formatDate(intake.deadline) : "-"}
                      </div>
                    </TableCell>
                    <TableCell>{getUserName(intake.assignedOwner)}</TableCell>
                    {canDelete && (
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Intake Queue</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this intake queue? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <BlackButton
                                onClick={() => handleDelete(intake.id)}
                                className="bg-red-500 hover:bg-red-600 text-white"
                              >
                                Delete
                              </BlackButton>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        {canReassign && intake.status === "rejected" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                            onClick={() => {
                              setSelectedIntake(intake);
                              setShowReassignDialog(true);
                            }}
                            title="Reassign"
                          >
                            <ArrowUpRight className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <AlertCircle className="w-8 h-8" />
                      <p>No intakes found</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <NewIntakeForm 
        open={showNewIntakeForm} 
        onOpenChange={setShowNewIntakeForm}
        users={users}
      />

      <AlertDialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign Rejected Intake</AlertDialogTitle>
            <AlertDialogDescription>
              This intake was rejected. Provide a note explaining why it's being reassigned back to the owner.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedIntake && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 font-medium">Rejection Reason:</p>
              <p className="text-sm text-amber-700 mt-1">{selectedIntake.rejectReason || "No reason provided"}</p>
            </div>
          )}
          <textarea
            value={reassignNote}
            onChange={(e) => setReassignNote(e.target.value)}
            placeholder="Enter note for reassigning this task..."
            className="w-full p-3 border border-slate-200 rounded-lg min-h-[100px]"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setSelectedIntake(null); setReassignNote(""); }}>Cancel</AlertDialogCancel>
            <BlackButton 
              onClick={handleReassign}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Reassign Task
            </BlackButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IntakeQueue;
